import { useState, useEffect, useRef } from 'react';
import { FlagImg } from '@/components/ui/CountryFlag';

// ─── DEV TEST MODE ────────────────────────────────────────────────────────────
const TEST_MODE = false;
const TEST_PHONE = '';
const TEST_COUNTRY_CODE = '+44';
const TEST_BYPASS_CODE = '000000';
// ─────────────────────────────────────────────────────────────────────────────
import { useTranslation } from 'react-i18next';
import { useOnboarding } from '../OnboardingContext';
import { ChevronDown, Search, Phone, Loader2, AlertTriangle, ArrowRight, CheckCircle2 } from 'lucide-react';
import { phoneCodes, phoneCodesSorted } from '@/data/phoneCodes';

import { useToast } from '@/hooks/use-toast';

export function PhoneStep() {
  const { t } = useTranslation();
  const { data, updateData, goToNextStep, setCustomBackHandler, setCurrentStep, currentStep } = useOnboarding();
  const { toast } = useToast();
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [filteredCountries, setFilteredCountries] = useState(phoneCodesSorted);
  const [isDetectingCountry, setIsDetectingCountry] = useState(false);
  
  // Track if user has manually selected a country code
  const userSelectedCountryRef = useRef(false);
  const detectionAttemptedRef = useRef(false);
  
  // OTP verification state
  const [codeSent, setCodeSent] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const checkTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Existing application detection
  const [existingAppEmail, setExistingAppEmail] = useState<string | null>(null);
  const [existingAppAction, setExistingAppAction] = useState<'login' | 'resume'>('login');
  
  // Local state for country code - start empty to allow detection
  const [localCountryCode, setLocalCountryCode] = useState<string>(data.countryCode || '');

  // TEST MODE: auto-fill phone on mount
  useEffect(() => {
    if (TEST_MODE && !phoneNumber) {
      setPhoneNumber(TEST_PHONE);
      setLocalCountryCode(TEST_COUNTRY_CODE);
      userSelectedCountryRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const selectedCountry = phoneCodes.find(c => c.code === localCountryCode) || phoneCodes.find(c => c.code === '+44') || phoneCodes[0];

  // Custom back handler for OTP view
  useEffect(() => {
    if (codeSent) {
      setCustomBackHandler(() => {
        setCodeSent(false);
        setCode(['', '', '', '', '', '']);
        setError('');
        return true;
      });
    } else {
      setCustomBackHandler(null);
    }
    
    return () => setCustomBackHandler(null);
  }, [codeSent, setCustomBackHandler]);

  // Country code to phone code mapping
  const countryToPhoneCode: Record<string, string> = {
    'PT': '+351', 'GB': '+44', 'IE': '+353', 'US': '+1', 'CA': '+1',
    'DE': '+49', 'FR': '+33', 'ES': '+34', 'IT': '+39',
    'NL': '+31', 'BE': '+32', 'AT': '+43', 'CH': '+41',
    'PL': '+48', 'SE': '+46', 'NO': '+47', 'DK': '+45',
    'FI': '+358', 'GR': '+30', 'CZ': '+420', 'HU': '+36',
    'RO': '+40', 'BG': '+359', 'HR': '+385', 'SK': '+421',
    'SI': '+386', 'LT': '+370', 'LV': '+371', 'EE': '+372',
    'CY': '+357', 'MT': '+356', 'LU': '+352', 'AU': '+61',
    'NZ': '+64', 'JP': '+81', 'KR': '+82', 'CN': '+86',
    'HK': '+852', 'SG': '+65', 'MY': '+60', 'TH': '+66',
    'ID': '+62', 'PH': '+63', 'VN': '+84', 'IN': '+91',
    'AE': '+971', 'SA': '+966', 'IL': '+972', 'TR': '+90',
    'ZA': '+27', 'BR': '+55', 'MX': '+52', 'AR': '+54',
    'CL': '+56', 'CO': '+57', 'PE': '+51',
  };

  // Auto-detect country from IP on mount using client-side geolocation
  useEffect(() => {
    // Reset refs on mount for fresh detection per flow
    userSelectedCountryRef.current = false;
    detectionAttemptedRef.current = false;
    
    const detectCountry = async () => {
      // Skip if user has manually selected during this session
      if (userSelectedCountryRef.current) return;
      
      // Skip if already have a valid country code from context (not default)
      if (data.countryCode && data.countryCode !== '+44' && !detectionAttemptedRef.current) {
        setLocalCountryCode(data.countryCode);
        return;
      }
      
      detectionAttemptedRef.current = true;
      setIsDetectingCountry(true);
      
      try {
        // Use client-side IP detection to get user's actual location
        const response = await fetch('https://ipapi.co/json/');
        const geoData = await response.json();
        
        // Double-check user hasn't selected while we were fetching
        if (userSelectedCountryRef.current) {
          return;
        }
        
        if (geoData?.country_code) {
          const phoneCode = countryToPhoneCode[geoData.country_code] || '+44';
          const detectedCountry = phoneCodes.find(c => c.code === phoneCode);
          if (detectedCountry) {
            setLocalCountryCode(phoneCode);
            updateData({ countryCode: phoneCode });
          } else {
            // Fallback to default if detected code not found
            setLocalCountryCode('+44');
          }
        } else {
          // Fallback to default on error
          setLocalCountryCode('+44');
        }
      } catch (err) {
        setLocalCountryCode('+44');
      } finally {
        setIsDetectingCountry(false);
      }
    };

    detectCountry();
  }, []);

  // Focus first OTP input when code sent
  useEffect(() => {
    if (codeSent) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [codeSent]);

  // Check for existing application when phone number changes
  // Only shows warning if the existing session has BOTH verified email AND password set
  // This prevents the loop where user can't proceed but also can't resume (no password)
  const checkExistingApplication = async (phone: string) => {
    if (phone.length < 7) {
      setExistingAppEmail(null);
      return;
    }
    
    setIsCheckingPhone(true);
    try {
      const sanitizedCountryCode = localCountryCode.replace(/[^\d+]/g, '');
      const sanitizedPhoneNumber = phone.replace(/\D/g, '');
      const fullPhoneNumber = `${sanitizedCountryCode}${sanitizedPhoneNumber}`;
      
      // Determine current account type for filtering
      const currentAccountType = data.accountType || 'individual';
      
      // Use direct fetch to avoid supabase.functions.invoke swallowing error bodies
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const resp = await fetch(`${supabaseUrl}/functions/v1/onboarding-session-lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
          'apikey': supabaseKey,
        },
        body: JSON.stringify({
          action: 'check_phone',
          phone_variants: [fullPhoneNumber, sanitizedPhoneNumber, `+${sanitizedPhoneNumber}`],
          account_type: currentAccountType,
        }),
      });
      
      if (resp.ok) {
        const lookupResult = await resp.json();
        if (lookupResult?.matching_email) {
          setExistingAppEmail(lookupResult.matching_email);
        } else {
          setExistingAppEmail(null);
        }
      } else {
        setExistingAppEmail(null);
      }
    } catch (err) {
      console.error('Error checking for existing application:', err);
      setExistingAppEmail(null);
    } finally {
      setIsCheckingPhone(false);
    }
  };

  // Debounced phone number check
  useEffect(() => {
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
    }
    
    if (phoneNumber.length >= 7 && !codeSent) {
      checkTimeoutRef.current = setTimeout(() => {
        checkExistingApplication(phoneNumber);
      }, 500); // 500ms debounce
    } else {
      setExistingAppEmail(null);
    }
    
    return () => {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, [phoneNumber, localCountryCode, codeSent]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleSendCode = async () => {
    if (phoneNumber.length < 7) return;

    // TEST MODE: bypass real SMS
    if (TEST_MODE) {
      updateData({ phoneNumber, countryCode: localCountryCode });
      setCodeSent(true);
      setResendTimer(30);
      toast({ title: '🧪 Test Mode', description: `Use code ${TEST_BYPASS_CODE} to verify` });
      return;
    }

    setIsSendingCode(true);
    setError('');
    setExistingAppEmail(null);
    
    try {
      updateData({ phoneNumber });
      
      // Use fetch directly to properly handle non-2xx responses with body
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const response = await fetch(`${supabaseUrl}/functions/v1/send-sms-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
          'apikey': supabaseKey,
        },
        body: JSON.stringify({
          phoneNumber,
          countryCode: localCountryCode,
          accountType: data.accountType || 'individual',
        }),
      });

      let result: any = {};
      try {
        result = await response.json();
      } catch {
        // If JSON parsing fails, use status text
      }

      if (!response.ok || result?.error) {
        // Check if it's an existing client / duplicate phone error (409)
        if (response.status === 409 || result?.existing_application || result?.is_existing_client) {
          setExistingAppEmail(result?.email || null);
          setExistingAppAction(result?.action === 'resume' ? 'resume' : 'login');
          setError(result?.error || 'This phone number is already registered. Please use a different number or log in.');
          return;
        }
        const errorMsg = result?.error || 'Failed to send verification code';
        setError(errorMsg);
        toast({
          title: 'Error',
          description: errorMsg,
          variant: 'destructive',
        });
        return;
      }

      setCodeSent(true);
      setResendTimer(30);
      toast({
        title: 'Code sent',
        description: 'Check your phone for the verification code',
      });
    } catch (err: any) {
      console.error('Error sending SMS:', err);
      const msg = err?.message?.includes('Edge Function') 
        ? 'This phone number may already be in use. Please try a different number.'
        : 'Failed to send verification code. Please try again.';
      setError(msg);
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) return;

    // TEST MODE: code 000000 bypasses real verification
    if (TEST_MODE && fullCode === TEST_BYPASS_CODE) {
      updateData({ phoneNumber, countryCode: localCountryCode, phoneOtpVerified: true });
      toast({ title: '🧪 Test Mode', description: 'Phone bypass accepted' });
      if (currentStep === 'corporate-phone') {
        setCurrentStep('corporate-email');
      } else {
        setCurrentStep('email');
      }
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const response = await fetch(`${supabaseUrl}/functions/v1/verify-sms-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
          'apikey': supabaseKey,
        },
        body: JSON.stringify({
          phoneNumber,
          countryCode: localCountryCode,
          otp: fullCode,
        }),
      });

      const result = await response.json();

      if (!response.ok || result?.error) {
        const errorMsg = result?.error || 'Invalid verification code';
        setError(errorMsg);
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
        setIsVerifying(false);
        return;
      }

      updateData({ phoneNumber, countryCode: localCountryCode, phoneOtpVerified: true });

      toast({ title: 'Verified', description: 'Your phone number has been verified' });
      setIsVerifying(false);

      if (currentStep === 'corporate-phone') {
        setCurrentStep('corporate-email');
      } else {
        setCurrentStep('email');
      }
    } catch (err) {
      console.error('Error verifying OTP:', err);
      setError('Failed to verify code. Please try again.');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    setCode(['', '', '', '', '', '']);
    setError('');
    await handleSendCode();
    inputRefs.current[0]?.focus();
  };

  const handleCountrySelect = (code: string) => {
    userSelectedCountryRef.current = true; // Mark as user-selected to prevent auto-detection overwrite
    setLocalCountryCode(code);
    updateData({ countryCode: code });
    setShowCountryPicker(false);
    setFilteredCountries(phoneCodesSorted); // Reset filter after selection
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.slice(0, 15);
  };

  const isCodeComplete = code.every(digit => digit !== '');

  // ── Already-verified bypass ──────────────────────────────────────────────
  // Only treat as already-verified if the phone has been explicitly confirmed
  // via OTP. Having a countryCode alone (e.g. from company search) must NOT bypass.
  const alreadyVerified = Boolean(data.phoneOtpVerified && data.phoneNumber && data.countryCode);

  // Auto-advance immediately — never show this step again once verified
  useEffect(() => {
    if (alreadyVerified) {
      goToNextStep();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayPhone = alreadyVerified
    ? `${data.countryCode} ${data.phoneNumber}`
    : null;

  if (alreadyVerified) {
    return (
      <div className="min-h-screen bg-background flex flex-col pt-24 pb-8 px-4 sm:px-6">
        <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
          <div className="mb-6">
            <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-6">
              <Phone className="w-8 h-8 text-accent" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              {t('phone.title')}
            </h1>
            <p className="text-muted-foreground">
              {t('phone.subtitle')}
            </p>
          </div>

          {/* Verified state card */}
          <div className="rounded-2xl border-2 border-accent/40 bg-accent/5 p-5 flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-6 h-6 text-accent" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-0.5">Phone Verified</p>
              <p className="text-base font-semibold text-foreground truncate">{displayPhone}</p>
            </div>
          </div>

          <button
            onClick={goToNextStep}
            className="w-full h-14 rounded-full bg-accent text-accent-foreground font-semibold flex items-center justify-center gap-2 hover:bg-accent/90 transition-all mt-auto"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col pt-24 pb-8 px-4 sm:px-6">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full overflow-y-auto overflow-x-hidden">
        <div className="mb-6">
          <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-6">
            <Phone className="w-8 h-8 text-accent" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {t('phone.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('phone.subtitle')}
          </p>
        </div>

        {/* Phone number input row */}
        <div className="relative space-y-2 mb-6">
          <label className="text-sm font-medium text-foreground">{t('phone.label')}</label>
          <div className="flex flex-col md:flex-row gap-2">
            <div className="flex gap-2 flex-1">
              <button
                onClick={() => !isDetectingCountry && setShowCountryPicker(!showCountryPicker)}
                disabled={isDetectingCountry}
                className="flex items-center gap-2 px-4 py-3 rounded-xl border border-border bg-background hover:bg-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 min-w-[100px]"
              >
                {isDetectingCountry ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <div className="w-5 h-5 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm">...</span>
                  </div>
                ) : (
                  <>
                    <FlagImg code={selectedCountry.iso2} size="md" />
                    <span className="font-medium text-sm">{selectedCountry.code}</span>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </>
                )}
              </button>
              
              <div className="relative flex-1">
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                  placeholder={t('phone.placeholder')}
                  className="input-field w-full pr-10"
                  autoFocus={!codeSent}
                />
                <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              </div>
            </div>
            
            {codeSent ? (
              <button
                onClick={() => {
                  setCodeSent(false);
                  setCode(['', '', '', '', '', '']);
                  setError('');
                  setResendTimer(0);
                }}
                className="px-4 py-3 text-sm font-medium rounded-xl border-2 border-border text-foreground hover:bg-secondary transition-colors whitespace-nowrap w-full md:w-auto flex items-center justify-center gap-2 min-w-[120px]"
              >
                Edit Number
              </button>
            ) : (
              <button
                onClick={handleSendCode}
                disabled={phoneNumber.length < 7 || isSendingCode || !!existingAppEmail || isCheckingPhone}
                className="px-4 py-3 text-sm font-medium rounded-xl border-2 border-accent text-accent hover:bg-accent/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap w-full md:w-auto flex items-center justify-center gap-2 min-w-[120px]"
              >
                {isSendingCode ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('phone.sending')}
                  </>
                ) : existingAppEmail ? (
                  t('phone.numberInUse')
                ) : (
                  t('phone.getCode')
                )}
              </button>
            )}
          </div>

          {/* Country picker dropdown */}
          {showCountryPicker && (
            <div className="absolute left-0 top-full mt-2 w-full max-w-72 bg-card rounded-xl shadow-xl border border-border z-50 overflow-hidden">
              <div className="p-2 border-b border-border">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder={t('common.searchCountry')}
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-secondary border-none focus:outline-none focus:ring-2 focus:ring-accent/50"
                    style={{ fontSize: '16px' }}
                    onChange={(e) => {
                      const search = e.target.value.toLowerCase();
                      const filtered = phoneCodesSorted.filter(c => 
                        c.country.toLowerCase().includes(search) || c.code.includes(search)
                      );
                      setFilteredCountries(filtered);
                    }}
                  />
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto overscroll-contain">
                {filteredCountries.map((country, index) => (
                  <button
                    key={`${country.code}-${country.country}-${index}`}
                    onClick={() => handleCountrySelect(country.code)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary active:bg-secondary/80 transition-colors text-left touch-list-item"
                  >
                    <FlagImg code={country.iso2} size="md" />
                    <span className="font-medium flex-1 truncate">{country.country}</span>
                    <span className="text-muted-foreground text-sm">{country.code}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Existing application warning */}
        {existingAppEmail !== null && (
          <div className="rounded-2xl border-2 border-warning bg-warning/10 p-5 space-y-4 animate-fade-in-up">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-warning/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-warning" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">
                  {existingAppAction === 'login' ? 'Phone Number Already Registered' : 'Application Already In Progress'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {error || t('phone.existingApp.description')}
                  {existingAppEmail && existingAppEmail !== 'null' && (
                    <span className="block mt-1 text-foreground">
                      Account: {existingAppEmail}
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            <div className="flex flex-col gap-2">
              <button
                onClick={() => {
                  window.location.href = existingAppAction === 'resume' ? '/app/login?resume=1' : '/app/login';
                }}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-all"
              >
                {existingAppAction === 'resume' ? 'Resume Existing Application' : 'Log in to your account'}
                <ArrowRight className="w-4 h-4" />
              </button>
              
              {/* Only allow "use different number" for non-client duplicates (e.g. in-progress apps).
                  Existing approved individual clients must log in — no bypass. */}
              {existingAppAction !== 'login' && (
              <button
                onClick={() => {
                  setExistingAppEmail(null);
                  setError('');
                  setPhoneNumber('');
                }}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-border text-foreground font-medium hover:bg-secondary transition-all text-sm"
              >
                Use a different number
              </button>
              )}
            </div>
            
            <p className="text-xs text-center text-muted-foreground">
              {existingAppAction === 'login'
                ? 'You already have an account with us. Please log in to access your account. If you need assistance, contact our support team.'
                : "You'll be asked to verify your identity to resume your application."}
            </p>
          </div>
        )}

        {/* OTP verification section */}
        <div className="rounded-2xl border border-border bg-card/50 p-4 sm:p-6 space-y-4">
          <p className="text-sm text-foreground">
            {t('phone.enterCode')}
          </p>
          
          <div className="flex flex-col gap-4">
            <div className="flex gap-2 justify-center">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled={!codeSent || isVerifying}
                  autoComplete={index === 0 ? 'one-time-code' : 'off'}
                  name={index === 0 ? 'otp' : undefined}
                  className={`w-10 h-12 sm:w-11 sm:h-14 text-center text-xl font-semibold border-2 rounded-xl focus:outline-none ${
                    codeSent 
                      ? 'border-border bg-background focus:border-accent' 
                      : 'border-border/50 bg-muted/30 text-muted-foreground cursor-not-allowed'
                  }`}
                />
              ))}
            </div>
            
            <button
              onClick={handleVerify}
              disabled={!isCodeComplete || !codeSent || isVerifying}
              className="w-full h-12 text-sm font-semibold rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isVerifying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('phone.verifying')}
                </>
              ) : (
                t('phone.verify')
              )}
            </button>
          </div>

          {error && (
            <p className="text-destructive text-sm">{error}</p>
          )}

          {codeSent && (
            <div className="flex items-center justify-end text-sm">
              {resendTimer > 0 ? (
                <span className="text-muted-foreground">{t('phone.resendIn', { seconds: resendTimer })}</span>
              ) : (
                <button 
                  onClick={handleResend} 
                  disabled={isSendingCode}
                  className="text-accent font-medium hover:underline disabled:opacity-50"
                >
                  {t('phone.resendCode')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
